import React, { useCallback, useEffect, useState } from 'react'
import { ExcelRenderer, ExcelResult, OutTable } from 'react-excel-renderer'
import { Box, LinearProgress } from '@mui/material'
import mime from 'mime'
import { toast } from 'react-hot-toast'
import { isExcelFile } from '../../../utils'

const defaultFilePreviewState = Object.freeze({
  blob: null,
  sheet: null,
  error: null
})

type FileViewerProps = {
  /**
   *  file *must* be defined when open is true
   */
  fileMeta?: {
    name: string
    type: string
  }

  fileData?: ArrayBuffer | null

  loadingProgress: number
}

async function readExcelFile(blob: Blob): Promise<ExcelResult> {
  return new Promise((resolve, reject) => {
    ExcelRenderer(blob, (err, result) => {
      if (err) {
        reject(err)
      } else {
        resolve(result)
      }
    })
  })
}

export const FileViewerContents = ({ fileMeta, fileData, loadingProgress }: FileViewerProps) => {
  const [filePreview, setFilePreview] = useState<{
    blob: string | null
    sheet: ExcelResult | null
    error: string | null
  }>(defaultFilePreviewState)

  const readBinaryFile = useCallback(async () => {
    setFilePreview({ ...defaultFilePreviewState })

    if (!fileMeta || !fileData) {
      return
    }

    console.log('reading binary file')

    if (!mime.getType(fileMeta.type) && !mime.getExtension(fileMeta.type)) {
      toast.error('Unable to preview file')
      return
    }

    try {
      const blob = new Blob([fileData], { type: fileMeta.type })

      if (isExcelFile(fileMeta)) {
        const result = await readExcelFile(blob)

        setFilePreview({
          blob: null,
          sheet: {
            rows: result.rows,
            cols: result.cols
          },
          error: null
        })
      } else {
        const objectUrl = URL.createObjectURL(blob)
        // if (viewFilePreference === 'NEW_TAB') {
        //   const el = document.createElement('a')
        //   el.href = objectUrl
        //   el.hidden = true
        //   el.target = '_blank'
        //   el.click()
        //   el.remove()
        // } else {
        setFilePreview({
          blob: objectUrl,
          sheet: null,
          error: null
        })
      }
    } catch (e) {
      toast.error(`Failed to read file. ${e}`)
      setFilePreview({
        blob: null,
        sheet: null,
        error: String(e)
      })
    }
  }, [fileMeta, fileData])

  useEffect(() => {
    console.log('fileData did change')

    readBinaryFile()
  }, [fileData?.byteLength])

  if (filePreview.error) {
    return <Box>{filePreview.error}</Box>
  }

  if (filePreview.sheet) {
    return (
      <OutTable
        data={filePreview.sheet.rows}
        columns={filePreview.sheet.cols}
        tableClassName="bg-white google-sheets-table"
        tableHeaderRowClass="google-sheets-table__header-row"
      />
    )
  }

  if (filePreview.blob) {
    return <iframe className="flex flex-grow border-none h-full" title={fileMeta!.name} src={filePreview.blob} />
  }

  return (
    <Box className="w-full flex items-center justify-center">
      <LinearProgress variant="determinate" value={loadingProgress} color="inherit" className="w-[400px] text-white" />
    </Box>
  )
}
